import { Bell, BellOff } from "lucide-react";
import { useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";

/** Wyłącznik w tej przeglądarce: kilka osób w jednym pokoju może wyciszyć wszystkie poza jedną. */
const STORAGE_KEY = "helpdesk-new-ticket-sound";
const listeners = new Set<() => void>();
// `storage`: przełącznik w jednej karcie od razu włącza sondę w tle w pozostałych.
const subscribe = (fn: () => void) => {
	listeners.add(fn);
	window.addEventListener("storage", fn);
	return () => {
		listeners.delete(fn);
		window.removeEventListener("storage", fn);
	};
};
// Domyślnie włączony; zapamiętujemy tylko wyłączenie ("0").
const isEnabled = () => localStorage.getItem(STORAGE_KEY) !== "0";

export function useNewTicketSound() {
	return useSyncExternalStore(subscribe, isEnabled);
}

function setEnabled(on: boolean) {
	if (on) localStorage.removeItem(STORAGE_KEY);
	else localStorage.setItem(STORAGE_KEY, "0");
	for (const fn of listeners) fn();
}

let ctx: AudioContext | undefined;

/** Dźwięk jest dodatkiem: brak Web Audio nie może wywrócić skrzynki. */
function audioContext(): AudioContext | undefined {
	try {
		ctx ??= new AudioContext();
		void ctx.resume();
		return ctx;
	} catch {
		return undefined;
	}
}

// Przeglądarka gra dopiero po geście na stronie. Po odświeżeniu z włączonym dzwonkiem pierwsze
// kliknięcie albo klawisz uruchamia kontekst, żeby sonda w tle mogła później zagrać.
const unlock = () => {
	if (ctx?.state !== "running" && isEnabled()) audioContext();
};
window.addEventListener("pointerdown", unlock, true);
window.addEventListener("keydown", unlock, true);

/** Dwutonowy „ding-dong” (E5 → A5, ok. 0,6 s) z Web Audio — bez pliku dźwiękowego. */
export function playNewTicketSound() {
	const audio = audioContext();
	if (!audio) return;
	const t0 = audio.currentTime;
	[659, 880].forEach((freq, i) => {
		const start = t0 + i * 0.2;
		const osc = audio.createOscillator();
		const gain = audio.createGain();
		osc.type = "sine";
		osc.frequency.value = freq;
		// Szybkie narastanie i wybrzmiewanie jak dzwonek, bez trzasku na końcu.
		gain.gain.setValueAtTime(0.0001, start);
		gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.4);
		osc.connect(gain).connect(audio.destination);
		osc.start(start);
		osc.stop(start + 0.42);
	});
}

/** Dzwonek w nagłówku skrzynki; włączenie od razu gra próbkę (i odblokowuje dźwięk w przeglądarce). */
export function NewTicketSoundToggle() {
	const on = useNewTicketSound();
	const title = on
		? "Dźwięk przy nowym zgłoszeniu i odpowiedzi apteki włączony — kliknij, aby wyłączyć. Po odświeżeniu strony zagra po pierwszym kliknięciu na niej."
		: "Włącz dźwięk przy nowym zgłoszeniu i odpowiedzi apteki w moim albo nieprzypisanym zgłoszeniu (działa też w karcie w tle)";
	return (
		<Button
			variant="outline"
			size="sm"
			className="h-7 shrink-0 px-2"
			aria-pressed={on}
			aria-label="Dźwięk przy nowym zgłoszeniu"
			title={title}
			onClick={() => {
				setEnabled(!on);
				if (!on) playNewTicketSound();
			}}
		>
			{on ? <Bell className="size-3.5" /> : <BellOff className="size-3.5 text-muted-foreground" />}
		</Button>
	);
}
